// packages imports
import { Fragment, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Divider, Grid, Typography, TextField, Box, MenuItem } from '@mui/material'

// components imports
import CheckboxController from '../../../common/CheckboxController'

// others imports
import { ExternalUserWithClientNumberInterface } from '../../../../interfaceTypes'
import { enumToLabel, titleize } from '../../../../utils'
import CustomMultiSelectChip from '../../../common/CustomMultiSelectChip'
import { BillDeliveryMethod, ExternalUserCaseRole } from '../../../../../generated'
import { CustomController } from '../../../common/CustomController'

interface ClientBillDeliveryRowProps {
  client: ExternalUserWithClientNumberInterface
  fieldPrefix: string
}

const ClientBillDeliveryRow = ({ client, fieldPrefix }: ClientBillDeliveryRowProps) => {
  const { watch, setValue } = useFormContext()

  const wantsBillDelivery: boolean = watch(`${fieldPrefix}.wantsBillDelivery`) ?? false
  const selectedMethods: BillDeliveryMethod[] = watch(`${fieldPrefix}.billDeliveryMethods`) ?? []

  useEffect(() => {
    if (!wantsBillDelivery) return

    if (!watch(`${fieldPrefix}.billingEmail`) && client.email) {
      setValue(`${fieldPrefix}.billingEmail`, client.email)
    }
    if (!watch(`${fieldPrefix}.billingPhoneNumber`) && client.workPhone) {
      setValue(`${fieldPrefix}.billingPhoneNumber`, client.workPhone)
    }
    if (!watch(`${fieldPrefix}.billingAddress`) && client.defaultBillingAddress) {
      setValue(`${fieldPrefix}.billingAddress`, client.defaultBillingAddress)
    }
    // eslint-disable-next-line
  }, [wantsBillDelivery])

  const caseRole = titleize(client?.caseRole?.replace('_', ' ') || '')
  const isDisabled = !wantsBillDelivery

  // displayName first: a business client stores its name in businessName and leaves
  // firstName/lastName empty, so building the name from those alone rendered a blank Client
  // field for every company. Same precedence the rest of the matter form uses.
  const clientName =
    client?.displayName?.trim() || `${client?.firstName || ''} ${client?.lastName || ''}`.trim()

  return (
    <Fragment key={`${client.id}-${caseRole}`}>
      <Grid item xs={2} md={1}>
        <CheckboxController name={`${fieldPrefix}.wantsBillDelivery`} label="" />
      </Grid>
      <Grid item xs={10} md={4}>
        <TextField
          value={clientName}
          label="Client"
          variant="outlined"
          disabled={isDisabled}
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <TextField
          value={enumToLabel(caseRole)}
          label="Matter Role"
          variant="outlined"
          disabled={isDisabled}
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={6} md={4}>
        <CustomMultiSelectChip
          name={`${fieldPrefix}.billDeliveryMethods`}
          label="Deliver Through"
          getLabel={value => titleize(value)}
          disabled={isDisabled}
          chipLabel={value => {
            const deliveryMethod = Object.values(BillDeliveryMethod).find(method => method === value)
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" fontSize={12}>
                  {titleize(deliveryMethod || '')}
                </Typography>
              </Box>
            )
          }}
          children={Object.values(BillDeliveryMethod)?.map(item => (
            <MenuItem value={item} key={item}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                <Typography variant="body1">{titleize(item)}</Typography>
              </Box>
            </MenuItem>
          ))}
        />
      </Grid>
      {!isDisabled && selectedMethods.includes(BillDeliveryMethod.Paper) && (
        <Grid item xs={12}>
          <Box ml={9}>
            <CustomController
              fieldType="text"
              controllerName={`${fieldPrefix}.billingAddress`}
              controllerLabel="Billing Address"
              isMultiLine
              rowsLength={3}
              sx={{ my: 0 }}
            />
          </Box>
        </Grid>
      )}
      {!isDisabled && selectedMethods.includes(BillDeliveryMethod.Email) && (
        <Grid item xs={12}>
          <Box ml={9}>
            <CustomController
              fieldType="text"
              controllerName={`${fieldPrefix}.billingEmail`}
              controllerLabel="Billing Email"
              sx={{ my: 0 }}
            />
          </Box>
        </Grid>
      )}
      {!isDisabled && selectedMethods.includes(BillDeliveryMethod.Text) && (
        <Grid item xs={12}>
          <Box ml={9}>
            <CustomController
              fieldType="text"
              controllerName={`${fieldPrefix}.billingPhoneNumber`}
              controllerLabel="Billing Phone Number"
              sx={{ my: 0 }}
            />
          </Box>
        </Grid>
      )}
    </Fragment>
  )
}

interface BillDeliveryFormProps {
  isEdit?: boolean
}

const BillDeliveryForm = ({ isEdit = false }: BillDeliveryFormProps) => {
  const { watch, setValue } = useFormContext()

  const primaryClient = { ...watch('client'), caseRole: ExternalUserCaseRole.PrimaryClient }
  const clientUsers = watch('clientUsers')?.concat(primaryClient) || []

  useEffect(() => {
    if (!primaryClient.billDeliveryMethods?.length && !isEdit) {
      setValue('client.wantsBillDelivery', true)
      setValue('client.billDeliveryMethods', [BillDeliveryMethod.Email])
    }

    // eslint-disable-next-line
  }, [])
  return (
    <Grid container item rowSpacing={2.5}>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" textTransform="uppercase" fontWeight={700}>
          Bill Delivery
        </Typography>
      </Grid>

      <Grid container item spacing={2.5} alignItems="center">
        {clientUsers.map((client: ExternalUserWithClientNumberInterface, index: number) => {
          if (!client?.id || !client?.caseRole) return null

          const fieldPrefix = client.caseRole === ExternalUserCaseRole.PrimaryClient ? 'client' : `clientUsers.${index}`

          return (
            <ClientBillDeliveryRow key={`${client.id}-${client.caseRole}`} client={client} fieldPrefix={fieldPrefix} />
          )
        })}
      </Grid>

      <Grid item xs={12}>
        <CustomController
          fieldType="text"
          controllerName="ccEmailBillsTo"
          controllerLabel="CC Email Bills To"
          sx={{ my: 0 }}
          placeholder="email1@example.com; email2@example.com"
        />
      </Grid>
    </Grid>
  )
}

export default BillDeliveryForm
